import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import Paginator from '../../components/Paginator';
import { toast } from 'react-toastify';
import { formatDate, formatCurrency, formatDateLocal } from '../../utils/helper';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const List = () => {
    const [purchases, setPurchases] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    
    const [searchParams] = useSearchParams();
    
    const [apiData, setApiData] = useState({
        search: "",
        page: 1,
        start_date: "",
        end_date: ""
    });

    useEffect(() => {
        getPurchases();
    }, [apiData.page, apiData.search, apiData.start_date, apiData.end_date]);

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setApiData(prev => ({
            ...prev,
            start_date: start ? formatDateLocal(start) : "",
            end_date: end ? formatDateLocal(end) : ""
        }));
    };

    const getPurchases = async () => {
        setIsLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/purchases',
                data: apiData
            });
            if (response.data.status) {
                // Check if the structure is data.purchases (paginated object) or data.purchases.data (array)
                // Based on previous read: setData(response.data.data.purchases.data);
                // So response.data.data.purchases is the paginator object.
                const result = response.data.data.purchases;
                setPurchases(result.data || []);
                setPagination({
                    total: result.total,
                    per_page: result.per_page,
                    current_page: result.current_page,
                    last_page: result.last_page,
                });
            }
        } catch (error) {
            toast.error('Failed to fetch purchases');
        } finally {
            setIsLoading(false);
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm('Are you sure you want to delete this purchase? This will revert stock changes if status is received.')) return;
        
        try {
            const response = await ApiService.request({
                method: 'DELETE',
                url: `admin/purchases/${id}`
            });
            if (response.data.status) {
                toast.success(response.data.message);
                getPurchases();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete purchase');
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Purchases</h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/purchases/create" className="btn btn-sm fw-bold btn-base">Create Purchase</Link>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <i className="fas fa-search fs-1"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search Purchase"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-3">
                                    <div className="w-200px">
                                         <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid"
                                            placeholderText="Select Date Range"
                                            isClearable={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            {isLoading ? <Loader /> : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-row-dashed fs-6 gy-5">
                                            <thead>
                                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                    <th>Date</th>
                                                    <th>Reference</th>
                                                    <th>Supplier</th>
                                                    <th>Status</th>
                                                    <th>Amount</th>
                                                    <th>Note</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-600 fw-semibold">
                                                {purchases.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{formatDate(item.purchase_date)}</td>
                                                        <td>{item.purchase_number}</td>
                                                        <td>{item.supplier?.name || item.supplier_name || '-'}</td>
                                                        <td>
                                                            <span className={`badge badge-light-${item.status === 'received' ? 'success' : 'warning'}`}>
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td>{formatCurrency(item.total_amount)}</td>
                                                        <td>{item.note}</td>
                                                        <td className="text-end">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit Purchase</Tooltip>}>
                                                                <Link to={`/purchases/edit/${item.id}`} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                                                                    <i className="fas fa-edit"></i>
                                                                </Link>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Delete Purchase</Tooltip>}>
                                                                <button onClick={() => deleteItem(item.id)} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {purchases.length === 0 && (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">No purchases found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Paginator pagination={pagination} onPageChange={(page) => setApiData({ ...apiData, page })} />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
